import React, { useState } from "react";
import { PlusIcon, XIcon } from "@phosphor-icons/react";
import { ChevronRight } from "lucide-react";
import { ScalarKind, FactValue } from "../types";
import ScalarInput from "./ScalarInput";
import { SimpleValueRenderer } from "../Components/FactRenderer";
import HierarchicalDropdown from "./HierarchicalDropdown";

interface HierarchicalArrayInputProps {
  scalarType: ScalarKind | null;
  scalarUnit?: string | null;
  scalarValues?: any[] | null;
  values: FactValue[];
  onAdd: (value: any[]) => void;
  onRemove: (index: number) => void;
  // Forwarded to the enum-case HierarchicalDropdown so its <button>
  // trigger paints pl-24 / pr-24 around overlaid Autofill / Source
  // chips. The non-enum builder branch doesn't need these — its
  // ScalarInput child already inherits the parent's `[&_input]:pl-24`
  // arbitrary-variant selectors.
  hasAutofillChip?: boolean;
  hasSourceChip?: boolean;
}

const HierarchicalArrayInput: React.FC<HierarchicalArrayInputProps> = ({
  scalarType,
  scalarUnit,
  scalarValues,
  values,
  onAdd,
  onRemove,
  hasAutofillChip = false,
  hasSourceChip = false,
}) => {
  const [buildingHierarchy, setBuildingHierarchy] = useState<any[]>([]);
  const [currentInput, setCurrentInput] = useState<any>(null);

  const handleAddLevel = () => {
    if (
      currentInput !== null &&
      currentInput !== undefined &&
      currentInput !== ""
    ) {
      setBuildingHierarchy([...buildingHierarchy, currentInput]);
      setCurrentInput(null);
    }
  };

  const handleRemoveLevel = (index: number) => {
    setBuildingHierarchy(buildingHierarchy.filter((_, i) => i !== index));
  };

  const handleSaveHierarchy = () => {
    if (buildingHierarchy.length > 0) {
      onAdd(buildingHierarchy);
      setBuildingHierarchy([]);
    }
  };

  return (
    <div className="space-y-3">
      {/* Display existing hierarchies. The trailing padding when a
          Source chip is overlaid keeps the right-most chip on each
          wrapped row from sliding under the Source button (which is
          absolutely positioned at top-2 right-2 of the parent
          EditableFactRenderer). */}
      {values.length > 0 && (
        <div className={`flex flex-wrap gap-2${hasSourceChip ? " pr-24" : ""}`}>
          {values.map((item, index) => (
            <div
              key={index}
              className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-800 rounded-md text-sm"
            >
              <SimpleValueRenderer
                value={item.scalar}
                scalarType={scalarType}
                scalarUnit={scalarUnit}
                isHierarchical={Array.isArray(item.scalar)}
              />
              <button
                onClick={() => onRemove(index)}
                className="hover:bg-blue-200 rounded p-0.5"
                type="button"
              >
                <XIcon size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* For enums, use dropdown. For non-enums, use builder */}
      {scalarValues && scalarValues.length > 0 ? (
        // Enum case - use dropdown to select from predefined values
        <HierarchicalDropdown
          scalarType={scalarType}
          scalarUnit={scalarUnit}
          options={scalarValues}
          value=""
          onChange={(value) => {
            if (value) {
              onAdd(value);
            }
          }}
          placeholder="Add an option"
          filterExisting={values.map((v) => v.scalar)}
          hasAutofillChip={hasAutofillChip}
          hasSourceChip={hasSourceChip}
        />
      ) : (
        // Non-enum case - use builder for creating new hierarchies
        <div className="border border-gray-200 rounded-md p-3 space-y-2">
          <div className="text-xs font-medium text-gray-700">
            Build New Hierarchy
          </div>

          {/* Current hierarchy being built */}
          {buildingHierarchy.length > 0 && (
            <div className="flex items-center flex-wrap gap-1 p-2 bg-gray-50 rounded">
              {buildingHierarchy.map((level, index) => (
                <React.Fragment key={index}>
                  {index > 0 && (
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  )}
                  <div className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-gray-200 rounded text-sm">
                    <SimpleValueRenderer
                      value={level}
                      scalarType={scalarType}
                      scalarUnit={scalarUnit}
                    />
                    <button
                      onClick={() => handleRemoveLevel(index)}
                      className="hover:bg-gray-100 rounded p-0.5"
                      type="button"
                    >
                      <XIcon size={12} className="text-gray-500" />
                    </button>
                  </div>
                </React.Fragment>
              ))}
            </div>
          )}

          {/* Input for next level */}
          <div className="flex gap-2">
            <div className="flex-1">
              <ScalarInput
                scalarType={scalarType}
                scalarUnit={scalarUnit}
                scalarValues={null} // Don't pass enum values to the builder
                value={currentInput}
                onChange={setCurrentInput}
                isHierarchical={false} // Prevent infinite recursion
                placeholder="Enter level value..."
              />
            </div>
            <button
              onClick={handleAddLevel}
              disabled={
                currentInput === null ||
                currentInput === undefined ||
                currentInput === ""
              }
              className="px-3 py-1.5 text-sm bg-gray-600 text-white rounded hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              type="button"
              title="Add level"
            >
              <PlusIcon size={14} />
            </button>
          </div>

          {/* Save hierarchy button */}
          {buildingHierarchy.length > 0 && (
            <button
              onClick={handleSaveHierarchy}
              className="w-full px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
              type="button"
            >
              Add Hierarchy to List
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default HierarchicalArrayInput;
